/**
 * Shared look-and-feel for the Vaperite staging screens: the staging dashboard
 * (pages/VaperiteStagingDashboard.tsx) and the webhook payloads tool
 * (pages/Tools/WebhookPayloads.tsx). Moved out of the dashboard unchanged.
 */

import React from 'react';
import { Box, Chip, Grid, Paper, Stack, Typography, alpha } from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  ErrorOutline as ErrorIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MonitorHeart as HealthIcon,
  Storage as StorageIcon,
  AccessTime as ClockIcon,
  BoltOutlined as BoltIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAdminTheme, AdminColors } from '../../admin-theme';

// ============================================================================
// Filter chips
// ============================================================================

/** A selected filter must be unmistakable (Jason, 26 Oct 2026: "just has a thin tiny outline"). */
export const SELECTED_CHIP_SX = { fontWeight: 800, boxShadow: '0 0 0 2px currentColor', transform: 'scale(1.05)' };

/** Spread onto a filter Chip: a tick, a ring and bold text while it is selected. */
export const selectedChipProps = (active: boolean) => ({
  icon: active ? <CheckIcon /> : undefined,
  'aria-pressed': active,
  sx: active ? SELECTED_CHIP_SX : undefined,
});

// ============================================================================
// Types
// ============================================================================

export interface DashboardMetrics {
  pipeline: {
    successRate: number;
    approvalRate: number | null;
    failureRate24h: number;
    avgProcessingSeconds: number;
    currentlyProcessing: number;
  };
  payloads: {
    total: number;
    today: number;
    last24h: number;
    failed24h: number;
    failedTotal: number;
    processedTotal: number;
    avgSizeKB: number;
    maxSizeKB: number;
    avgItemsPerPayload: number;
    maxItemsPerPayload: number;
    avgCategoriesPerPayload: number;
    menuSyncTotal: number;
    webhookTotal: number;
    lastPayloadAt: string | null;
  };
  stores: {
    total: number;
    active24h: number;
    topStores: Array<{
      storeId: string;
      storeName: string;
      totalPayloads: number;
      failures: number;
      todayCount: number;
      lastActivity: string;
    }>;
  };
  items: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    withDiscrepancies: number;
    criticalErrors: number;
    uniqueSkus: number;
    avgBacklogAgeHours: number;
    lastStagedAt: string | null;
    stagedToday: number;
    reviewedToday: number;
  };
  review: {
    avgReviewTimeHours: number;
    fastestReviewHours: number;
    slowestReviewHours: number;
    totalReviewed: number;
    reviewedToday: number;
  };
  fieldChanges: Array<{
    field: string;
    total: number;
    pending: number;
    today: number;
  }>;
  hourlyActivity: Array<{
    hour: string;
    payloads: number;
    items: number;
    failures: number;
  }>;
}

// ============================================================================
// CSS Keyframes (injected once)
// ============================================================================

const keyframesInjected = (() => {
  if (typeof document === 'undefined') return true;
  const id = 'skc-staging-keyframes';
  if (document.getElementById(id)) return true;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `
    @keyframes skc-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes skc-glow-pulse { 0%, 100% { box-shadow: 0 0 8px var(--glow-color); } 50% { box-shadow: 0 0 20px var(--glow-color), 0 0 40px var(--glow-color); } }
    @keyframes skc-live-dot { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.5); opacity: 0.6; } }
    @keyframes skc-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
    @keyframes skc-error-pulse { 0%, 100% { background-color: rgba(255,0,60,0.04); } 50% { background-color: rgba(255,0,60,0.12); } }
    @keyframes skc-count-pop { 0% { transform: scale(1); } 50% { transform: scale(1.15); } 100% { transform: scale(1); } }
  `;
  document.head.appendChild(style);
  return true;
})();

export function formatTimeAgo(date: string | null): string {
  if (!date) return 'Never';
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function formatNumber(n: number): string {
  return n.toLocaleString('en-ZA');
}

// ============================================================================
// Glow Summary Card
// ============================================================================

export interface GlowCardProps {
  icon: React.ReactElement;
  label: string;
  value: number | string;
  color: string;
  subtitle?: string;
  pulse?: boolean;
  onClick?: () => void;
  active?: boolean;
}

export function GlowCard({ icon, label, value, color, subtitle, pulse, onClick, active }: GlowCardProps) {
  const { colors } = useAdminTheme();
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        bgcolor: active ? alpha(color, 0.08) : colors.bgCard,
        border: `1px solid ${active ? alpha(color, 0.5) : colors.border}`,
        borderRadius: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: colors.bgElevated,
          borderColor: alpha(color, 0.4),
          boxShadow: `0 0 30px ${alpha(color, 0.15)}, inset 0 0 30px ${alpha(color, 0.05)}`,
          transform: 'translateY(-2px)',
        },
        '&::before': pulse ? {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          animation: 'skc-shimmer 2s linear infinite',
          backgroundSize: '200% 100%',
        } : {},
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(color, 0.12),
          color,
          flexShrink: 0,
          transition: 'all 0.3s',
          boxShadow: `0 0 15px ${alpha(color, 0.1)}`,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 26 } } as any)}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: colors.textPrimary,
            fontWeight: 800,
            lineHeight: 1.2,
            mt: 0.25,
            ...(pulse ? { animation: 'skc-count-pop 0.5s ease' } : {}),
          }}
        >
          {typeof value === 'number' ? formatNumber(value) : value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Mini Metric Tile (for System Health rows)
// ============================================================================

export function MetricTile({ icon, label, value, unit, color, description, trend }: {
  icon: React.ReactElement;
  label: string;
  value: string | number;
  unit?: string;
  color: string;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
}) {
  const { colors } = useAdminTheme();
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        borderLeft: `3px solid ${color}`,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: colors.bgElevated,
          borderColor: alpha(color, 0.3),
          boxShadow: `0 0 20px ${alpha(color, 0.08)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
        <Box sx={{ color: alpha(color, 0.7), display: 'flex' }}>
          {React.cloneElement(icon, { sx: { fontSize: 16 } } as any)}
        </Box>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography sx={{ color: colors.textPrimary, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1.1 }}>
          {value}
        </Typography>
        {unit && (
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem', fontWeight: 500 }}>
            {unit}
          </Typography>
        )}
        {trend && trend !== 'neutral' && (
          <Box sx={{ color: trend === 'up' ? colors.success : colors.error, display: 'flex', ml: 0.5 }}>
            {trend === 'up' ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
          </Box>
        )}
      </Stack>
      {description && (
        <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.65rem', mt: 0.5, display: 'block' }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

// ============================================================================
// System Health Panel
// ============================================================================

export function SystemHealthPanel({ metrics, variant }: { metrics: DashboardMetrics | null; variant: 'items' | 'webhooks' }) {
  const { colors } = useAdminTheme();
  if (!metrics) return null;

  const getHealthColor = (rate: number) => rate >= 95 ? colors.success : rate >= 80 ? colors.warning : colors.error;
  const getHealthLabel = (rate: number) => rate >= 95 ? 'Healthy' : rate >= 80 ? 'Degraded' : 'Critical';

  if (variant === 'webhooks') {
    const healthColor = getHealthColor(metrics.pipeline.successRate);
    return (
      <Paper sx={{
        p: 2.5,
        mb: 2.5,
        bgcolor: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, ${healthColor}, ${alpha(healthColor, 0.3)}, transparent)`,
        },
      }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <HealthIcon sx={{ fontSize: 18, color: healthColor }} />
          <Typography sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.85rem' }}>
            System Health
          </Typography>
          <Chip
            label={getHealthLabel(metrics.pipeline.successRate)}
            size="small"
            sx={{
              bgcolor: alpha(healthColor, 0.12),
              color: healthColor,
              fontWeight: 700,
              fontSize: '0.6rem',
              height: 20,
              border: `1px solid ${alpha(healthColor, 0.3)}`,
            }}
          />
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
            Webhook pipeline &amp; processing metrics
          </Typography>
        </Stack>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<SpeedIcon />}
              label="Success Rate"
              value={`${metrics.pipeline.successRate}%`}
              color={getHealthColor(metrics.pipeline.successRate)}
              description="Payloads processed without errors"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<TimerIcon />}
              label="Avg Processing"
              value={metrics.pipeline.avgProcessingSeconds < 60
                ? metrics.pipeline.avgProcessingSeconds
                : Math.round(metrics.pipeline.avgProcessingSeconds / 60)}
              unit={metrics.pipeline.avgProcessingSeconds < 60 ? 'sec' : 'min'}
              color={colors.info}
              description="Avg time to process a payload"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<StorageIcon />}
              label="Avg Payload"
              value={metrics.payloads.avgSizeKB}
              unit="KB"
              color={colors.purple}
              description={`Max: ${formatNumber(metrics.payloads.maxSizeKB)} KB`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<BoltIcon />}
              label="Today"
              value={metrics.payloads.today}
              unit="payloads"
              color={colors.info}
              description={`${metrics.stores.active24h} stores active (24h)`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<ErrorIcon />}
              label="Failures (24h)"
              value={metrics.payloads.failed24h}
              color={metrics.payloads.failed24h > 0 ? colors.error : colors.success}
              description={`${metrics.pipeline.failureRate24h}% failure rate`}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <MetricTile
              icon={<InventoryIcon />}
              label="Avg Items"
              value={metrics.payloads.avgItemsPerPayload}
              unit="per payload"
              color={colors.success}
              description={`Max: ${formatNumber(metrics.payloads.maxItemsPerPayload)} items`}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  }

  // variant === 'items'
  const approvalRate = metrics.pipeline.approvalRate;
  const approvalColor = approvalRate === null ? colors.textMuted : approvalRate >= 80 ? colors.success : approvalRate >= 50 ? colors.warning : colors.error;

  return (
    <Paper sx={{
      p: 2.5,
      mb: 2.5,
      bgcolor: colors.bgCard,
      border: `1px solid ${colors.border}`,
      borderRadius: 2,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: `linear-gradient(90deg, ${colors.info}, ${alpha(colors.info, 0.3)}, transparent)`,
      },
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <SpeedIcon sx={{ fontSize: 18, color: colors.info }} />
        <Typography sx={{ color: colors.textPrimary, fontWeight: 700, fontSize: '0.85rem' }}>
          Review Pipeline
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
          Staging item review metrics &amp; backlog
        </Typography>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<ApproveIcon />}
            label="Approval Rate"
            value={approvalRate !== null ? `${approvalRate}%` : '--'}
            color={approvalColor}
            description="Approved vs rejected items"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<ClockIcon />}
            label="Avg Review Time"
            value={metrics.review.avgReviewTimeHours || '--'}
            unit={metrics.review.avgReviewTimeHours ? 'hrs' : undefined}
            color={colors.info}
            description={metrics.review.fastestReviewHours > 0 ? `Fastest: ${metrics.review.fastestReviewHours}h` : 'No reviews yet'}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<TimerIcon />}
            label="Backlog Age"
            value={metrics.items.avgBacklogAgeHours || '--'}
            unit={metrics.items.avgBacklogAgeHours ? 'hrs' : undefined}
            color={metrics.items.avgBacklogAgeHours > 48 ? colors.error : metrics.items.avgBacklogAgeHours > 12 ? colors.warning : colors.success}
            description="Avg time items sit pending"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<InventoryIcon />}
            label="Staged Today"
            value={metrics.items.stagedToday}
            color={colors.info}
            description={`${metrics.items.reviewedToday} reviewed today`}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<WarningIcon />}
            label="Discrepancies"
            value={metrics.items.withDiscrepancies}
            color={metrics.items.withDiscrepancies > 0 ? colors.warning : colors.success}
            description="Multi-store data mismatches"
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <MetricTile
            icon={<ErrorIcon />}
            label="Critical Errors"
            value={metrics.items.criticalErrors}
            color={metrics.items.criticalErrors > 0 ? colors.error : colors.success}
            description="Items with processing errors"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

// ============================================================================
// Table Styles
// ============================================================================

export const darkTableSx = {
  '& .MuiTableHead-root .MuiTableCell-root': {
    bgcolor: AdminColors.bgCard,
    color: AdminColors.textMuted,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    fontSize: '0.7rem',
    letterSpacing: '0.06em',
    borderBottom: `1px solid ${AdminColors.border}`,
    py: 1.5,
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    bgcolor: AdminColors.bgCard,
    transition: 'all 0.15s',
    '&:hover': {
      bgcolor: `${AdminColors.bgElevated} !important`,
    },
  },
  '& .MuiTableCell-root': {
    color: AdminColors.textPrimary,
    borderColor: AdminColors.border,
    py: 1.25,
  },
  '& .MuiCheckbox-root': {
    color: AdminColors.textMuted,
    '&.Mui-checked': { color: AdminColors.info },
  },
};
